/*
 * $Id:: AddressCard.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';

import { GLOBALS_SLICE_NAME } from '../../../../app/constants';
import { countries as countriesSlice, states as statesSlice } from '../../../../app/globalsSlice';
import inputsMaxChars from '../../../../app/inputsMaxChars';
import { ReactComponent as CheckedIcon } from '../../../../assets/images/check_box_24px.svg';
import { ReactComponent as UncheckedIcon } from '../../../../assets/images/check_box_outline_blank_24px.svg';
import { ReactComponent as DropDownArrow } from '../../../../assets/images/unfold_more_24px.svg';
import { isNumber, isEmail } from '../../../../common/utils/service';
import Card from '../../../../components/Card';
import ErrorInput from '../../../../components/ErrorInput';
import { t } from '../../../../providers';
import { loadingAddress } from '../../accountSlice';

const useStyles = makeStyles((theme) => ({
    cardTitle: {
        fontSize: '0.875rem',
        fontWeight: '600',
        color: theme.palette.text.outlined,
        marginTop: '10px',
    },
    fieldLabel: {
        color: theme.palette.text.label,
        fontSize: '0.625rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 'normal',
    },
    fieldValue: {
        color: theme.palette.text.value,
        fontSize: '0.75rem',
        letterSpacing: 'normal',
        marginBottom: '10px',
    },
    linkValue: {
        color: theme.palette.text.linkBlue,
        fontSize: '0.75rem',
        letterSpacing: 'normal',
        marginBottom: '10px',
        textDecoration: 'underline',
    },
    textInput: {
        height: '2rem',
        width: '9rem',
        borderRadius: '2px',
        marginTop: '5px',
        marginBottom: '10px',
        '@media (max-width:600px)': {
            width: '9.5rem',
        },
    },
    selectWidth: {
        width: '12rem',
    },
    selectSmallWidth: {
        width: '11rem',
    },
    checkboxLabel: {
        '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
        },
    },
    checkboxDisabled: {
        '& path': {
            fill: theme.palette.disabled.button,
        },
    },
    corrAddressTitle: {
        marginLeft: '-10px',
        marginBottom: '24px',
        marginTop: '20px',
    },
    inputError: {
        marginTop: '-5px',
        marginBottom: '5px',
    },
    disabledMenuItem: {
        color: '#5D5D5D',
        opacity: '1 !important',
    },
}));

const AddressCard = ({ initialValues, saveDetails, hasAdministratorRole }) => {
    const classes = useStyles();
    const countries = useSelector(countriesSlice);
    const loading = useSelector(loadingAddress);
    const dispatch = useDispatch();
    const states = useSelector(statesSlice);

    const [readonly, setReadonly] = useState(true);

    const [legAddress, setLegAddress] = useState({});

    const [triggerInitialData, setTriggerInitialData] = useState(false); // trigger to get initial data

    const [corrAddress, setCorrAddress] = useState({});

    const [hasCorrAddress, setHasCorrAddress] = useState(!!initialValues.correspondentAddress);

    useEffect(() => {
        if (!countries || !countries.length) dispatch({ type: `${GLOBALS_SLICE_NAME}/fetchCountries` });

        const { legalAddress, correspondentAddress } = initialValues || {};
        const lAddr = legalAddress || {};
        const cAddr = correspondentAddress || {};
        const newLegAddress = {
            streetName: {
                value: lAddr.streetName || '',
                placeholder: 'type.street.name',
                label: 'street.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.streetName,
            },
            streetNumber: {
                value: lAddr.streetNumber || '',
                placeholder: 'type.street.number',
                label: 'street.number',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.streetNumber,
            },
            city: {
                value: lAddr.city || '',
                placeholder: 'type.city',
                label: 'city',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.city,
            },
            postalCode: {
                value: lAddr.postalCode || '',
                placeholder: 'type.postal.code',
                label: 'postal.code',
                isDirty: false,
                element: 'Input',
                maxChars: inputsMaxChars.postalCode,
            },
            country: {
                value: lAddr.country || '',
                placeholder: 'select.country',
                label: 'country',
                isDirty: false,
                element: 'Select',
                options: countries,
                required: true,
            },
            state: {
                value: lAddr.state || '',
                placeholder: 'select.state',
                label: 'state',
                isDirty: false,
                element: 'Select',
                noIterrate: !lAddr.state,
            },
        };

        /* show states's field, if the selected legal Country has states */
        let finalLegAddress = { ...newLegAddress };
        if (finalLegAddress.country && finalLegAddress.country.value) {
            const statesOfCountry = !!states && states.find((s) => s.countryCode === finalLegAddress.country.value);
            const statesOptions = (!!statesOfCountry && statesOfCountry.state) || [];
            finalLegAddress.state.noIterrate = !statesOfCountry;
            finalLegAddress.state.options = statesOfCountry ? statesOptions : [];
            if (!statesOfCountry) finalLegAddress.state.value = '';
        }

        let newCorrAddress = {
            corrName: {
                value: cAddr.corrName || '',
                placeholder: 'type.correspondence.address.name',
                label: 'correspondence.address.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.corrName,
            },
            streetName: {
                value: cAddr.streetName || '',
                placeholder: 'type.street.name',
                label: 'street.name',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.streetName,
            },
            streetNumber: {
                value: cAddr.streetNumber || '',
                placeholder: 'type.street.number',
                label: 'street.number',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.streetNumber,
            },
            city: {
                value: cAddr.city || '',
                placeholder: 'type.city',
                label: 'city',
                isDirty: false,
                element: 'Input',
                required: true,
                maxChars: inputsMaxChars.city,
            },
            postalCode: {
                value: cAddr.postalCode || '',
                placeholder: 'type.postal.code',
                label: 'postal.code',
                isDirty: false,
                element: 'Input',
                maxChars: inputsMaxChars.postalCode,
            },
            country: {
                value: cAddr.country || '',
                placeholder: 'select.country',
                label: 'country',
                isDirty: false,
                element: 'Select',
                options: countries,
                required: true,
            },
            state: {
                value: cAddr.state || '',
                placeholder: 'select.state',
                label: 'state',
                isDirty: false,
                element: 'Select',
                noIterrate: !cAddr.state,
            },
            corrTelephone: {
                value: cAddr.corrTelephone || '',
                placeholder: 'type.correspondence.telephone',
                label: 'correspondence.telephone',
                isDirty: false,
                element: 'Input',
                isLinkType: true,
                maxChars: inputsMaxChars.corrTelephone,
            },
            corrEmail: {
                value: cAddr.corrEmail || '',
                placeholder: 'type.correspondence.email',
                label: 'correspondence.email',
                isDirty: false,
                element: 'Input',
                isLinkType: true,
                maxChars: inputsMaxChars.corrEmail,
            },
        };

        /* show states's field, if the selected correspondence Country has states */
        let finalCorrAddress = { ...newCorrAddress };
        if (finalLegAddress.country && finalLegAddress.country.value) {
            const statesOfCountry = !!states && states.find((s) => s.countryCode === finalCorrAddress.country.value);
            const statesOptions = (!!statesOfCountry && statesOfCountry.state) || [];
            finalCorrAddress.state.noIterrate = !statesOfCountry;
            finalCorrAddress.state.options = statesOfCountry ? statesOptions : [];
            if (!statesOfCountry) finalCorrAddress.state.value = '';
        }

        setReadonly(true);
        setLegAddress(finalLegAddress);
        setCorrAddress(finalCorrAddress);
        setHasCorrAddress(!!correspondentAddress);
    }, [initialValues, countries, dispatch, states, triggerInitialData]);

    const getCountryOrStateName = (code, options) => {
        const country = options.find((c) => c.code === code) || {};
        return country.value || code;
    };

    const getAddressValues = (fields) =>
        Object.keys(fields).reduce((acc, cur) => (fields[cur].isDirty ? { ...acc, [cur]: fields[cur].value } : { ...acc }), {});

    const cancelCard = (event) => {
        event.preventDefault();

        setTriggerInitialData(!triggerInitialData);
        setReadonly(true);
    };

    const saveCard = (event) => {
        event.preventDefault();

        const newLegalAddress = getAddressValues(legAddress);
        const newCorrAddress = hasCorrAddress ? getAddressValues(corrAddress) : null;

        // validate legAddress for errors & required fields
        const hasErrorsLegAddress = Object.keys(legAddress).some((field) => !!legAddress[field].errorMessage);
        let legAddressUpdatedWithErrorsMessage = { ...legAddress };
        const hasRequiredValuesLegAddress = Object.fromEntries(
            Object.entries(legAddressUpdatedWithErrorsMessage).filter(([key, fieldObject]) => {
                let result = false;
                if (fieldObject.required && fieldObject.value === '') {
                    legAddressUpdatedWithErrorsMessage[key].errorMessage = 'required.field';
                    result = true;
                }
                return result;
            })
        );
        if (Object.keys(hasRequiredValuesLegAddress).length > 0) setLegAddress(legAddressUpdatedWithErrorsMessage);

        // validate corrAddress for errors & required fields
        let hasErrorsCorrAddress = true;
        let hasRequiredValuesCorrAddress = {};
        let corrAddressUpdatedWithErrorsMessage = { ...legAddress };

        if (hasCorrAddress) {
            hasErrorsCorrAddress = Object.keys(corrAddress).some((field) => !!corrAddress[field].errorMessage);
            corrAddressUpdatedWithErrorsMessage = { ...corrAddress };
            hasRequiredValuesCorrAddress = Object.fromEntries(
                Object.entries(corrAddressUpdatedWithErrorsMessage).filter(([key, fieldObject]) => {
                    let result = false;
                    if (fieldObject.required && fieldObject.value === '') {
                        corrAddressUpdatedWithErrorsMessage[key].errorMessage = 'required.field';
                        result = true;
                    }
                    return result;
                })
            );
            if (Object.keys(hasRequiredValuesCorrAddress).length > 0) setCorrAddress(corrAddressUpdatedWithErrorsMessage);
        }

        // set canProceed for legAddress
        let canProceed = !hasErrorsLegAddress && Object.keys(hasRequiredValuesLegAddress).length <= 0;
        // set canProceed for corrAddress, if enabled
        if (canProceed && hasCorrAddress) canProceed = !hasErrorsCorrAddress && Object.keys(hasRequiredValuesCorrAddress).length <= 0;

        if (canProceed) {
            const payload = { legalAddress: { ...newLegalAddress, addressType: 'LEGAL' } };
            if (newCorrAddress) payload.correspondentAddress = { ...newCorrAddress, addressType: 'CORRESPONDENT' };
            payload.personDetails = { corrAddress: !!newCorrAddress };

            saveDetails(payload, 'Address');
        }
    };

    const handleValueChange = (event, setter, address) => {
        const value = event.target.value;
        const keyName = event.target.name;
        const field = { ...address[keyName], value, isDirty: true };
        if (!field.maxChars || (field.maxChars && value.length <= field.maxChars)) {
            let errorMessage = null;
            if (field.required && !value) errorMessage = 'required.field';
            if (value) {
                if (keyName === 'corrTelephone' && !isNumber(value)) errorMessage = 'invalid.format';
                if (keyName === 'corrEmail' && !isEmail(value)) errorMessage = 'invalid.email.format';
            }
            field.errorMessage = errorMessage;

            const newAddressValue = { ...address, [keyName]: field };

            if (event.target.name === 'country') {
                const statesOfCountry = !!states && states.find((s) => s.countryCode === value);
                const statesOptions = (!!statesOfCountry && statesOfCountry.state) || [];
                newAddressValue.state.noIterrate = !statesOfCountry;
                newAddressValue.state.options = statesOfCountry ? statesOptions : [];
                if (!statesOfCountry) newAddressValue.state.value = '';
            }

            setter({ ...newAddressValue });
        }
    };

    const handleCheckChange = (event) => {
        setHasCorrAddress(event.target.checked);
    };

    const createAddressBlock = (values, setter, idPrefix) => {
        return (
            <>
                {Object.keys(values)
                    .filter((f) => !values[f].noIterrate)
                    .map((keyName, idx) => {
                        const { label, placeholder, value, options, element, isLinkType, required = false, errorMessage } = values[keyName];

                        const isCode = ['country', 'state'].includes(keyName);
                        let selectOptions = options;
                        if (keyName === 'state') {
                            const cntry = values.country.value;
                            const countryStates = !!states && states.find((s) => s.countryCode === cntry);
                            selectOptions = (!!countryStates && countryStates.state) || [];
                        }
                        const commonProps = {
                            id: `${idPrefix}_${idx}_${keyName}`,
                            placeholder: t(placeholder),
                            name: keyName,
                            onChange: (e) => handleValueChange(e, setter, values),
                            value: value || '',
                        };
                        const valueClass = !isLinkType || !value ? classes.fieldValue : classes.linkValue;
                        const diplayedValue = (isCode ? getCountryOrStateName(value, selectOptions || []) : value) || '-';
                        const inputOrDropdown =
                            element === 'Input' ? (
                                <Input
                                    {...commonProps}
                                    className={classes.textInput}
                                    type="text"
                                    error={!!errorMessage}
                                    fullWidth
                                    autoFocus={idx === 0}
                                />
                            ) : (
                                <Select
                                    {...commonProps}
                                    className={`${classes.textInput} ${classes.selectWidth}`}
                                    displayEmpty
                                    error={!!errorMessage}
                                    IconComponent={DropDownArrow}
                                    inputProps={{ 'aria-haspopup': 'listbox' }}>
                                    <MenuItem value="" disabled className={classes.disabledMenuItem} role="option">
                                        {t(placeholder)}
                                    </MenuItem>
                                    {(selectOptions || []).map((option, idx) => (
                                        <MenuItem key={`${option.key}_${idx}`} value={option.code} role="option">
                                            {option.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            );

                        return (
                            <div key={`${idPrefix}_${keyName}_${idx}`}>
                                {readonly ? (
                                    <React.Fragment>
                                        <Typography className={classes.fieldLabel}>
                                            {t(label)} {required && ' *'}
                                        </Typography>
                                        <Typography className={valueClass}>{diplayedValue}</Typography>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <InputLabel error={!!errorMessage} htmlFor={keyName} className={classes.fieldLabel}>
                                            {t(label)} {required && ' *'}
                                        </InputLabel>
                                        {inputOrDropdown}
                                        {!!errorMessage && <ErrorInput errorMessage={t(errorMessage)} classes={`${classes.inputError}`} />}
                                    </React.Fragment>
                                )}
                            </div>
                        );
                    })}
            </>
        );
    };

    const renderItems = (
        <>
            {createAddressBlock(legAddress, setLegAddress, 'legalAddress')}
            <Grid item>
                <FormControlLabel
                    className={classes.checkboxLabel}
                    control={
                        <Checkbox
                            role="checkbox"
                            checked={hasCorrAddress}
                            className={readonly ? classes.checkboxDisabled : ''}
                            name="hasCorrAddress"
                            onChange={handleCheckChange}
                            checkedIcon={<CheckedIcon />}
                            icon={<UncheckedIcon />}
                            disabled={readonly}
                        />
                    }
                    label={t('correspondence.address.checkbox.label')}
                />
            </Grid>
            {hasCorrAddress && (
                <>
                    <Grid justify="space-between" alignItems="center" container className={classes.corrAddressTitle}>
                        <Grid item>
                            <Typography className={classes.cardTitle}>{t('correspondece.address.title')}</Typography>
                        </Grid>
                    </Grid>
                    {createAddressBlock(corrAddress, setCorrAddress, 'corrAddress')}
                </>
            )}
        </>
    );

    return (
        <Card
            title={'legal.address.title'}
            canEdit={hasAdministratorRole}
            readonly={readonly}
            editCard={() => setReadonly(false)}
            saveCard={saveCard}
            cancelCard={cancelCard}
            cardId={'profile_address_card'}>
            {loading ? <CircularProgress /> : renderItems}
        </Card>
    );
};

export default AddressCard;
